import { PureComponent } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { keys, groupBy, uniq, filter } from 'lodash'
import { Col, Row } from 'react-bootstrap'
import pluralize from 'pluralize'
import { giftTrackerDataFetcher } from 'registry/components/gift-tracker-data-fetcher'
import { ExpandIcon, EmailIcon, PresentIcon } from 'components/icons'
import { Input } from 'components/forms'
import Link from 'components/link'
import { LinkButton } from 'components/buttons'
import RegistrySideNavContainer from 'registry/components/registry-sidenav'
import { GIFT_TRACKER } from 'registry/constants'
import RegItemCardGhostList from 'registry/components/reg-item-card-ghost'

import * as blUrls from 'lib/urls'
import GiftReturnModal from './gift-returns/gift-return-modal'
import UnreserveModal from './unreserve-modal'
import css from './gift-tracker.scss'
import GiftGiverWrapper from './gift-giver-wrapper'
import { SectionHeading } from '../../components/headings'

class GiftTracker extends PureComponent {
  state = {
    expanded: false,
    searching: false,
    query: null,
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.isPageReady !== this.props.isPageReady ||
      prevProps.reservations !== this.props.reservations
    ) {
      this.setState({
        reservations: this.props.reservations,
      })
    }
  }

  resetFilter() {
    this.setState({
      searching: false,
      query: null,
      reservations: this.props.reservations,
    })
  }

  handleSearchFilter = (event) => {
    const { reservations } = this.props
    const query = event.target.value

    if (query.length > 0) {
      this.setState({
        searching: true,
        query,
        reservations: filter(reservations, (res) => {
          const search = new RegExp(query, 'gi')
          return (
            search.test(res.regItem.title) ||
            search.test(res.name) ||
            search.test(res.email)
          )
        }),
      })
    } else {
      this.resetFilter()
    }
  }

  renderInputAddon = () => {
    const { searching } = this.state
    let inputAddon = <i className="prm fa fa-search" />
    if (searching) {
      inputAddon = (
        <LinkButton
          className="pas input-group-addon--submit"
          onClick={() => this.resetFilter()}
        >
          Clear
        </LinkButton>
      )
    }
    return inputAddon
  }

  render() {
    const { cancelReservation, isPageReady, giftsPurchasedCount } = this.props
    const { reservations, expanded, searching, query } = this.state

    const reservationsByGiftGiver = groupBy(reservations, (res) => [
      res.email,
      res.name,
    ])
    const reservationEmails = keys(reservationsByGiftGiver)
    const emails = uniq(reservationEmails)

    return (
      <div className="container mts mtl-sm pbxl" style={{ minHeight: '40vh' }}>
        <Row>
          <Col sm={3} md={3} xl={2} className="hidden-xs">
            <RegistrySideNavContainer
              activeNav={GIFT_TRACKER}
              isContributorView
            />
          </Col>
          <Col sm={9} md={9} xl={10}>
            <div className={css.header}>
              <div>
                <SectionHeading className="mvn mbs">
                  Gift Tracker
                </SectionHeading>
                <div className="h6 mvm">
                  {giftsPurchasedCount > 0 && (
                    <>
                      <strong>{giftsPurchasedCount}</strong>
                      {` ${pluralize('Gift', giftsPurchasedCount)} Purchased`}
                    </>
                  )}
                  {giftsPurchasedCount > 0 && (
                    <>
                      <span className="phm hidden-xs">|</span>
                      <Link
                        className="hidden-xs text-brand-info"
                        url="/registry/gift-tracker.csv"
                      >
                        <EmailIcon width="16" style={{ marginTop: -2 }} />{' '}
                        Download Thank You List
                      </Link>
                    </>
                  )}
                </div>
              </div>
              <div className={classNames(css.actionBar)}>
                <Input
                  addonAfter={this.renderInputAddon()}
                  addonStyle={{ borderRadius: '0px 25px 25px 0px' }}
                  className="brn pll"
                  groupClassName={classNames(
                    css.search,
                    'mbn input-search input-search--bordered'
                  )}
                  onChange={this.handleSearchFilter}
                  placeholder="Search"
                  aria-label="Search"
                  value={query}
                />
                <LinkButton
                  className="mll pan"
                  onClick={() => {
                    this.setState({ expanded: !expanded })
                  }}
                >
                  <ExpandIcon />
                </LinkButton>
              </div>
            </div>

            <div className={classNames(css.heading, 'hidden-xs')}>
              <span>Name</span>
              <span>Date</span>
              <span>Gifts</span>
            </div>

            {isPageReady ? (
              <GiftGiverList
                emails={emails}
                reservationsByGiftGiver={reservationsByGiftGiver}
                show={searching || expanded}
              />
            ) : (
              <RegItemCardGhostList />
            )}

            {isPageReady && reservations && reservations.length == 0 && (
              <div
                className="well flex col center paxl mtl"
                style={{ minHeight: 300 }}
              >
                <PresentIcon />
                {searching ? (
                  <div className="h3 mtm ptn">No gifts matching "{query}"</div>
                ) : (
                  <>
                    <div className="h3 mtm ptn">No gifts yet!</div>
                    <p className="text-center">
                      Once your friends and family start buying gifts, they'll
                      appear here!
                    </p>
                  </>
                )}
              </div>
            )}

            {isPageReady && (
              <div className="mvxl text-center">
                <Link url={blUrls.loopGiftPath} className="h6 text-underline">
                  Return a gift from the Babylist shop that doesn't appear in
                  Gift Tracker
                </Link>
              </div>
            )}
          </Col>
        </Row>

        <GiftReturnModal />
        <UnreserveModal onUnreserve={cancelReservation} />
      </div>
    )
  }
}

const GiftGiverList = ({ emails, reservationsByGiftGiver, show }) => {
  const giftGiverList = []

  emails.map((email) => {
    giftGiverList.push(
      <GiftGiverWrapper
        key={email}
        reservations={reservationsByGiftGiver[email]}
        show={show}
      />
    )
  })

  return <>{giftGiverList}</>
}

GiftTracker.propTypes = {
  cancelReservation: PropTypes.func.isRequired,
  reservations: PropTypes.array,
}

export default giftTrackerDataFetcher(GiftTracker)
